import { useCallback, useEffect, useState } from "react";
import { FlaskConical, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import "@/components/teamproj/TeamProjections.css";
import "@/components/season/SeasonModelArena.css";

const STATS = [["pts", "PTS"], ["reb", "REB"], ["ast", "AST"], ["fg3m", "3PM"], ["pra", "PRA"]];
const n3 = (v) => (v == null ? "—" : v.toFixed(3));

// Admin: enrich the legacy x4 player model with the opponent-difficulty band + back-to-back rules
// (kept SEPARATE from the Season/Rotation model). Flag-gated, measured vs plain x4 by a backtest.
export function X4RulesCard() {
  const [status, setStatus] = useState(null);
  const [bt, setBt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const loadStatus = useCallback(() => api.get("/x4-context-rules").then((r) => setStatus(r.data)).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const toggle = async (enabled) => {
    setBusy(true);
    try { await api.put("/x4-context-rules", { enabled }); await loadStatus(); toast.success(enabled ? "Οι κανόνες εφαρμόζονται ζωντανά στο x4" : "Επαναφορά: καθαρό x4"); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const runBacktest = async () => {
    setRunning(true);
    try { const r = await api.get("/x4-context/backtest"); setBt(r.data); }
    catch (e) { toast.error(errorText(e)); } finally { setRunning(false); }
  };

  if (!status) return null;
  return (
    <div className="tp-root mt-6">
      <div className="tp-card" data-testid="x4-rules-card">
        <h4><FlaskConical size={13} className="inline mr-1" />ΚΑΝΟΝΕΣ ΣΤΟ x4 <small>(admin · opponent difficulty band A–F + back-to-back −{Math.round((1 - status.b2b_factor) * 100)}% · ξεχωριστά από το Season Model)</small></h4>
        <label className="x4-toggle" data-testid="x4-rules-toggle" style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0", color: "#cfe4fb", fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={status.enabled} disabled={busy} onChange={(e) => toggle(e.target.checked)} />
          <span>{status.enabled ? "Ενεργοί ζωντανά (γραμμές παικτών, σελίδα παίκτη, Value Scanner)" : "Ανενεργοί — το x4 μένει καθαρό (προεπιλογή)"}</span>
        </label>
        <p className="tp-note">Εμπλουτίζει το x4 με τους ίδιους κανόνες δυσκολίας/κόπωσης του Rotation Model. Τα δύο μοντέλα παραμένουν ξεχωριστά· καμία αλλαγή στον τύπο, στα ιστορικά ή στο Season Model. Τρέξε πρώτα το backtest για να δεις αν βελτιώνει.</p>

        <div className="tv-vactions">
          <button type="button" className="tv-vbtn primary" onClick={runBacktest} disabled={running} data-testid="x4-backtest-run"><Play size={14} />{running ? "Υπολογισμός…" : "Backtest: καθαρό x4 vs x4 + κανόνες"}</button>
        </div>

        {bt && bt.recommendation && (
          <div data-testid="x4-recommendation" style={{ marginTop: 12, padding: "10px 14px", borderRadius: 9, fontSize: 13,
            background: bt.recommendation.enable ? "rgba(34,197,94,.14)" : "rgba(148,163,184,.12)",
            border: `1px solid ${bt.recommendation.enable ? "rgba(34,197,94,.4)" : "rgba(148,163,184,.3)"}`,
            color: bt.recommendation.enable ? "#4ade80" : "#9fb6cf" }}>
            {bt.recommendation.enable ? "✅ Προτείνεται ενεργοποίηση. " : "ℹ️ "}{bt.recommendation.reason}
            {bt.recommendation.enable && !status.enabled && (
              <button type="button" className="tv-vbtn primary" style={{ marginLeft: 10 }} onClick={() => toggle(true)} disabled={busy} data-testid="x4-recommendation-enable">Ενεργοποίηση τώρα</button>
            )}
          </div>
        )}

        {bt && (
          <div className="aa-table-scroll" style={{ marginTop: 12, overflowX: "auto" }}>
            <table className="smx-table" data-testid="x4-backtest-table" style={{ minWidth: 520 }}>
              <thead><tr><th className="l">Stat</th><th>x4 MAE</th><th>x4+κανόνες MAE</th><th>x4 bias</th><th>x4+κανόνες bias</th></tr></thead>
              <tbody>
                {STATS.map(([k, l]) => {
                  const s = bt.stats[k]; if (!s) return null;
                  return (
                    <tr key={k} data-testid={`x4-bt-row-${k}`}>
                      <td className="l">{l}</td>
                      <td>{n3(s.plain_mae)}</td>
                      <td style={{ color: s.better ? "#4ade80" : "#f87171", fontWeight: 800 }}>{n3(s.rules_mae)} {s.better ? "▲" : "▼"}</td>
                      <td>{s.plain_bias > 0 ? "+" : ""}{n3(s.plain_bias)}</td>
                      <td>{s.rules_bias > 0 ? "+" : ""}{n3(s.rules_bias)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="tp-note">{bt.season_label} · n={bt.n} player-games. Πράσινο = οι κανόνες μειώνουν το MAE. Το bias πιο κοντά στο 0 είναι καλύτερο.</p>
          </div>
        )}
      </div>
    </div>
  );
}
